import { AfterViewInit, Component, inject, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule, NgForm, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { Feature } from 'ol';
import * as OlFormats from 'ol/format';
import { Geometry } from 'ol/geom';
import * as OlProj from 'ol/proj';
import { fromCircle } from 'ol/geom/Polygon';
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import { MapService } from '../../services/map.service';
import { OlLayerService } from '../../services/ol/ol-layer.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { ShareMapModule } from '../share-map/share-map.module';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';
import { NotificationService } from '@tt-lib/services/notification.service';
import { AlertService } from '@tt-lib/services/alert.service';
import { FileViewer } from '@capacitor/file-viewer';
import { MatSnackBar } from '@angular/material/snack-bar';

export enum FileDownloaderSupportedFileTypes {
  GEOJSON = 'geojson',
  GPX = 'gpx',
  KML = 'kml',
  /*
  not tested enough, qgis has problems
  GML = 'gml',
  */
  WKT = 'wkt'
};
export enum FileDownloaderSupportedProjectionTypes {
  WGS84 = 'EPSG:4326 (wgs 84)',
  LAMBERT2008 = 'EPSG:3812 (lambert 2008)',
  WEBMERCATOR = 'EPSG:3857 (web mercator)'
};
export type FileDownloaderData = {
  vectorLayerIds: string[];
  title?: string;
  description?: string;
  fileName?: string;
  selectedFileType?: FileDownloaderSupportedFileTypes
  selectedFileTypeOnly?: boolean;
  metadata?: any;
}
@Component({
  selector: 'tt-file-downloader',
  imports: [
    CommonModule,
    ShareMapModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    // FileDownloaderModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    ReactiveFormsModule,
    TranslateModule,
    FormsModule
  ],
  templateUrl: './file-downloader.component.html',
  styleUrls: ['./file-downloader.component.scss'],
  standalone: true
})
export class FileDownloaderComponent {
  readonly defaultFileType = FileDownloaderSupportedFileTypes.GEOJSON;
  readonly defaultProjection = FileDownloaderSupportedProjectionTypes.WGS84;
  fileNameSuggestion: string;
  errorMessage: string;

  @ViewChild('fileDownloadForm') private _fileDownloadForm: NgForm;
 public data = inject<FileDownloaderData>(MAT_DIALOG_DATA);
 
  constructor(
    private _olLayerService: OlLayerService,
    private _mapService: MapService,
    private _translateService: TranslateService,
    private _dialogRef: MatDialogRef<FileDownloaderComponent>,
    private alertService: AlertService,
    private _snackBar: MatSnackBar,
    //@Inject(MAT_DIALOG_DATA) public data: FileDownloaderData,
  ) {
    if (typeof this.data.fileName === "string") {
      //normalize name: remove apos & quote, remove double spaces, trim, ...?
      this.data.fileName = this.data.fileName.replace(/['"`´]/g, '').replace(/\s{2,}/g, ' ').trim();
      //check max length
      this.data.fileName = this.data.fileName.length > 100 ? this.data.fileName.substring(0, 100) : this.data.fileName;
      //test filename regex which is used by this component (see pattern in html template)
      this.data.fileName = /^((?!\s{2})[A-Za-zÀ-ÿ0-9\s_\-()\[\]]){1,100}$/.test(this.data.fileName) ? this.data.fileName : null;
    }
    const date = new Date();
    this.fileNameSuggestion = 'ngi_download_' + date.getFullYear() + '_' + ((date.getMonth() + 1) < 10 ? '0' : '') + (date.getMonth() + 1) + '_' + (date.getDate() < 10 ? '0' : '') + date.getDate()
    this.errorMessage = null;
  }

  fileDownloaderSupportedFileTypes() {
    const keys = Object.keys(FileDownloaderSupportedFileTypes);
    return keys.map(el => Object(FileDownloaderSupportedFileTypes)[el]);
  }

  fileDownloaderSupportedProjectionTypes() {
    const selectedFileType = this._fileDownloadForm && this._fileDownloadForm.value && this._fileDownloadForm.value.filetype ? this._fileDownloadForm.value.filetype : this.defaultFileType;
    if (selectedFileType === FileDownloaderSupportedFileTypes.GPX || selectedFileType === FileDownloaderSupportedFileTypes.KML || selectedFileType === FileDownloaderSupportedFileTypes.WKT) {
      return [FileDownloaderSupportedProjectionTypes.WGS84];
    } else {
      const keys = Object.keys(FileDownloaderSupportedProjectionTypes);
      return keys.map(el => Object(FileDownloaderSupportedProjectionTypes)[el]);
    }
  }

  getCheckedProjection() {
    const supportedProjections = this.fileDownloaderSupportedProjectionTypes();
    const currentValue = this._fileDownloadForm && this._fileDownloadForm.value && this._fileDownloadForm.value.projection ? this._fileDownloadForm.value.projection : this.defaultProjection;
    let useCurrentValue = false;
    supportedProjections.every((supportedProjection) => {
      if (currentValue === supportedProjection) {
        useCurrentValue = true;
        return false;
      }
      return true;
    });
    return useCurrentValue ? currentValue : supportedProjections[0];
  }

  async downloadFile(): Promise<void> {
    if (this._fileDownloadForm.valid) {
      this.errorMessage = null;
      let fileData = null;

      try {
        fileData = this._exportFeaturesToText();
      } catch (error) {
        console.log(`ERROR: an error occured during the transformation to format "${this._fileDownloadForm.value.filetype}" : ` + error);
        this.errorMessage = this._translateService.instant('file-downloader.error') + ' (' + error + ')';
      }

      //&& window['saveAs']
      if (fileData && typeof fileData.text === 'string' && fileData.text.length > 0 && typeof fileData.contentType === 'string' && fileData.contentType.length > 0) {
        try {
          //destination subfolders path without leading or trailing slashes
          //the rest of the code already has checks for destinationPath  
          let destinationPath = '';

          if (Capacitor.getPlatform() === 'ios') {
            //destinationPath = 'xxx/xxx/xxx';

            const fileName = `${this._fileDownloadForm.value.filename}.${this._fileDownloadForm.value.filetype}`;
            console.log('start download ios : ' + fileName)

            // Write file to Documents folder 
            const uriResult = await Filesystem.writeFile({
              path: destinationPath + (destinationPath.length === 0 ? '' : '/') + fileName,
              data: fileData.text,
              directory: Directory.Documents,
              encoding: Encoding.UTF8,
              recursive: destinationPath.length !== 0
            });

            console.log('result: ', uriResult.uri)

            const path = 'Documents' + (destinationPath.length === 0 ? '' : '/') + destinationPath;
            this._snackBar.open(`${fileName} has been saved to ${path}`, 'ok', {
              duration: 5500,
              horizontalPosition: 'center',
              verticalPosition: 'top'
            });

            //aint working anymore?
            // this.alertService.info(`${fileName} has been saved to Documents`, { 'autoclose': true })
            // this.alertService.info(this._translateService.instant('trace.create-route.auto-routing-busy'), { 'autoClose': true });
            //no fileviewer, only share...
            // await FileViewer.openDocumentFromLocalPath({
            //   path: uriResult.uri
            // });

            await Share.share({
              title: 'GPX File',
              //text: `${fileName}`,
              //dialogTitle: 'Share File',
              url: uriResult.uri,
              //files: [uriResult.uri],
            }).catch(err => { console.log('share: ', err) });

          } else if (Capacitor.getPlatform() === 'android') {
            //destinationPath = 'xxx/xxx/xxx';

            let fileName = `${this._fileDownloadForm.value.filename}.${this._fileDownloadForm.value.filetype}`;
            console.log('start download android : checking if filename already exists : ' + fileName);

            let renameCount = 0;
            let fileExists = true;
            while (fileExists && renameCount < 100) {
              try {
                const result = await Filesystem.stat({
                  path: `Download/${destinationPath + (destinationPath.length === 0 ? '' : '/') + fileName}`,
                  directory: Directory.ExternalStorage,
                });
                fileExists = true;
              } catch (error) {
                //does not exist or not accessible
                //console.error('file doesn\'t exist or is not accessible', error);
                fileExists = false;
              }

              if(fileExists) {
                renameCount++;
                fileName = `${this._fileDownloadForm.value.filename} (${renameCount}).${this._fileDownloadForm.value.filetype}`;
              }
            }

            //if it still exists after x retrials, use date as suffix
            if(fileExists) {
                let suffix = new Date().toISOString().replaceAll('-', '').replaceAll('T', '_').replaceAll(':', '').replaceAll('Z', '');
                suffix = suffix.substring(0, suffix.lastIndexOf('.'));
                fileName = `${this._fileDownloadForm.value.filename} (${suffix}).${this._fileDownloadForm.value.filetype}`;
            }

            console.log('start download android : checked filename : ' + fileName);

            //First option: write file to Documents folder, internal storage
            /*
            const uriResult = await Filesystem.writeFile({
              path: destinationPath + (destinationPath.length === 0 ? '' : '/') + fileName,
              data: fileData.text,
              directory: Directory.Documents,
              encoding: Encoding.UTF8,
              recursive: destinationPath.length !== 0
            });
            */

            //Second option: save in cache folder, internal storage, then copy to download folder, external storage 
            const uriResult0 = await Filesystem.writeFile({
              path: fileName,
              data: fileData.text,
              directory: Directory.Cache,
              encoding: Encoding.UTF8,
              recursive: false
            });
            //read from internal cache
            const readFile = await Filesystem.readFile({
              path: fileName,
              directory: Directory.Cache,
              encoding: Encoding.UTF8
            });
            //copy to Shared Storage (e.g., the Downloads folder)
            const uriResult = await Filesystem.writeFile({
              path: `Download/${destinationPath + (destinationPath.length === 0 ? '' : '/') + fileName}`, // Prepending 'Download/' puts it in the public folder
              data: readFile.data,          // This is the base64 data
              directory: Directory.ExternalStorage,
              encoding: Encoding.UTF8,
              recursive: true
            });

            console.log('result: ', uriResult.uri)

            //const path = 'Documents' + (destinationPath.length === 0 ? '' : '/') + destinationPath + ' (internal storage)';
            const path = 'Download' + (destinationPath.length === 0 ? '' : '/') + destinationPath;

            this._snackBar.open(`${fileName} has been saved to ${path}`, 'ok', {
              duration: 5500,
              horizontalPosition: 'center',
              verticalPosition: 'top'
            });

            await Share.share({
              title: 'GPX File',
              //text: `${fileName}`,
              //dialogTitle: 'Share File',
              url: uriResult.uri,
              //files: [uriResult.uri],
            }).catch(err => { console.log('share: ', err) });

            /*
           alernative for share popup
           await FileViewer.openDocumentFromLocalPath({
             path: uriResult.uri
           });
           */

          } else {
            var blob = new Blob([fileData.text], { type: fileData.contentType });
            var a = document.createElement('a');
            a.href = window.URL.createObjectURL(blob);
            a.download = this._fileDownloadForm.value.filename + '.' + this._fileDownloadForm.value.filetype;
            a.click();
            //via file-saver plugin
            //window['saveAs'](blob, this._fileDownloadForm.value.filename + '.' + this._fileDownloadForm.value.filetype);
          }

          this._dialogRef.close();

        } catch (error) {
          console.log('ERROR: an error occured during the download process : ' + error);
          this.errorMessage = this._translateService.instant('file-downloader.error') + ' (' + error + ')';
        }
      }
    }
  }



  private async shareFile(base64: string, filename: string) {
    await Share.share({
      title: filename,
      text: 'Here’s your tmv map',
      url: `data:application/pdf;name=${filename};base64,${base64}`,
      dialogTitle: 'Share PDF',
    });
  }

  private _exportFeaturesToText(): { text: string, contentType: string } {
    let fileData = {
      text: '',
      contentType: ''
    }

    //const features: Feature<Geometry>[] = [];
    const features = [];

    this.data.vectorLayerIds.forEach(vectorLayerId => {
      const vectorLayer = this._olLayerService.getLayerById(vectorLayerId);
      const tempFeatures: Feature<Geometry>[] = vectorLayer ? (vectorLayer as VectorLayer<any>).getSource().getFeatures() : [];
      let clonedFeature: Feature<Geometry>;

      if (tempFeatures.length === 0) {
        return fileData;
      }

      for (var i = 0; i < tempFeatures.length; i++) {
        if (!this._isValidFeatureForExport(tempFeatures[i], vectorLayerId)) {
          continue;
        }

        clonedFeature = tempFeatures[i].clone();
        clonedFeature.setStyle(null);
        clonedFeature.setId(tempFeatures[i].getId());

        this._removeKnownTTPropertiesFromFeature(clonedFeature, vectorLayerId, this._fileDownloadForm.value.filetype)

        features.push(clonedFeature);
      }

    });

    //we convert circles to polygons
    for (var i = 0; i < features.length; i++) {
      if (features[i].getGeometry().getType() === 'Circle') {
        features[i].setGeometry(fromCircle(features[i].getGeometry(), 360, 0));
        features[i].set('tt_circle_export', true);
      }
    }

    const outputProjectionCode = this._fileDownloadForm.value.projection.substring(0, this._fileDownloadForm.value.projection.indexOf(' ')).trim().toUpperCase();
    const writerOptions = {
      dataProjection: OlProj.get(outputProjectionCode),
      featureProjection: this._mapService.getCurrentMap().getView().getProjection()
    };

    switch (this._fileDownloadForm.value.filetype) {
      case FileDownloaderSupportedFileTypes.GEOJSON: {
        const writer = new OlFormats.GeoJSON();
        const geoJson = writer.writeFeaturesObject(features, writerOptions);
        geoJson['crs'] = {
          'type': 'name',
          'properties': { 'name': outputProjectionCode }
        };
        fileData.text = JSON.stringify(geoJson);
        fileData.contentType = 'application/geo+json;charset=utf-8';
        break;
      }
      /*
      case FileDownloaderSupportedFileTypes.GML: {
        const writer = new OlFormats.GML({
          featureNS: 'tt_export',
          featureType: 'tt_export_feature',
          srsName: outputProjectionCode
          //,curve: true 
        });
        const gmlOutput = writer.writeFeaturesNode(features, writerOptions);
        fileData.text = this._xmlToString(gmlOutput);
        //also possible
        //fileData.text = writer.writeFeatures(features, writerOptions);
        fileData.contentType = 'application/xml;charset=utf-8';
        break;
      }
      */
      case FileDownloaderSupportedFileTypes.GPX: {
        var writer = new OlFormats.GPX();
        const gpxOutput = writer.writeFeaturesNode(features, writerOptions);
        fileData.text = this._xmlToString(gpxOutput);
        if (typeof fileData.text === 'string' && fileData.text.indexOf('>') > -1) {
          //keep it simple, text inserts
          if (fileData.text.substring(0, fileData.text.indexOf('>')).indexOf('creator="') > -1) {
            const temp = fileData.text.substring(0, fileData.text.indexOf('creator="') + 9) + 'NGI-IGN BE';
            fileData.text = fileData.text.substring(fileData.text.indexOf('creator="') + 9);
            fileData.text = temp + fileData.text.substring(fileData.text.indexOf('"'));
          } else {
            fileData.text = fileData.text.substring(0, fileData.text.indexOf('>')) + ' creator="NGI-IGN BE"' + fileData.text.substring(fileData.text.indexOf('>'));
          }

          //name and description are repeated in the metadata tag and in the rte/trk tag because e.g. garmin devices don't read the name/desc tags inside the metadata tag
          //for now there are no seperate names and despriptions for metadata tag and the rte/trk tag(s)
          //name/desc tags have the same order inside metadata/rte/trk so we can create the text string here for all tags
          let nameAndDescription = '';
          if (this.data.metadata) {
            nameAndDescription = typeof this.data.metadata.name === 'string' && this.data.metadata.name.length > 0 ? '<name>' + this.data.metadata.name + '</name>' : '';
            //metadata desc tag cannot contain html tags, so replacing < and > by html entities
            nameAndDescription += typeof this.data.metadata.desc === 'string' && this.data.metadata.desc.length > 0 ? '<desc>' + this.data.metadata.desc.replaceAll('<', '&lt;').replaceAll('>', '&gt;') + '</desc>' : '';
          }

          if (fileData.text.substring(0, fileData.text.indexOf('>')).indexOf('xsi:schemaLocation="') > -1) {
            const gpxExtensionsVersion = "v1_1";
            let temp = fileData.text.substring(0, fileData.text.indexOf('xsi:schemaLocation="') + 20);
            fileData.text = fileData.text.substring(fileData.text.indexOf('xsi:schemaLocation="') + 20);
            temp += fileData.text.substring(0, fileData.text.indexOf('"'));
            temp += ' https://topomapviewer.ngi.be/xmlschemas/tracegpxextensions/' + gpxExtensionsVersion + ' https://common.ngi.be/static/xmlschemas/tmv_trace_gpxextensions_' + gpxExtensionsVersion + '.xsd" xmlns:tmv="https://topomapviewer.ngi.be/xmlschemas/tracegpxextensions/' + gpxExtensionsVersion + '"';
            fileData.text = fileData.text.substring(fileData.text.indexOf('"') + 1);
            fileData.text = temp + fileData.text;
            temp = '';

            //until now we have one route or one track
            for (var i = 0; i < features.length; i++) {
              if (features[i].getGeometry().getType() === 'LineString' && typeof features[i].get('gpx_extensions') === 'object' && fileData.text.indexOf('<rte>') > -1 && fileData.text.indexOf('</rte>') > fileData.text.indexOf('<rte>')) {
                //gpx route
                //for now we convert route to track
                //in the future we could add an extra dropdown where the user can choose 'export as gpx route' or 'export as gpx track'
                //and then we need to add an if/else inside this if

                /*
                if(user wants to keep route) {

                  const gpx_extensions: {} = features[i].get('gpx_extensions');
                  let rte_extension = "";

                  for (let key in gpx_extensions) {
                    switch (key) {
                      case 'rte_fixedpoint_indexes': {
                        //second parameter true
                        rte_extension +=  this._convertFixedPointIndexesToGpxExtensionTag(gpx_extensions[key], true);
                        break;
                      }
                      //add other extensions if necessary
                    }
                  }

                  //at this moment we don't need to check if there is already a name/desc in the tag, because during import name/desc are removed (possibly first copied to metadata, see importGpxFile)
                  fileData.text = fileData.text.substring(0, fileData.text.indexOf('<rte>') + 5) + nameAndDescription + (rte_extension.length > 0 ? '<extensions>' + rte_extension + '</extensions>' : '') + fileData.text.substring(fileData.text.indexOf('<rte>') + 5);
                } else { 
                  //user wants a track
                  //start of block that converts route to track
                */

                const gpx_extensions: {} = features[i].get('gpx_extensions');
                let rte_extension = '';

                for (let key in gpx_extensions) {
                  switch (key) {
                    case 'rte_fixedpoint_indexes': {
                      //second parameter false
                      rte_extension += this._convertFixedPointIndexesToGpxExtensionTag(gpx_extensions[key], false);
                      break;
                    }
                    //add other extensions if necessary
                  }
                }

                temp = fileData.text.substring(fileData.text.indexOf('<rte>') + 5, fileData.text.indexOf('</rte>'));
                //at this moment we don't need to check if there is already a name/desc in the tag, because during import name/desc are removed (possibly first copied to metadata, see importGpxFile)
                temp = '<trk>' + nameAndDescription + '<trkseg>' + temp + (rte_extension.length > 0 ? '<extensions>' + rte_extension + '</extensions>' : '') + '</trkseg></trk>';
                temp = temp.replaceAll('<rtept', '<trkpt').replaceAll('</rtept', '</trkpt');

                fileData.text = fileData.text.substring(0, fileData.text.indexOf('<rte>')) + temp + fileData.text.substring(fileData.text.indexOf('</rte>') + 6);

                /*
                  //end of block that converts route to track
                }
                */
              } else if (features[i].getGeometry().getType() === 'MultiLineString' && typeof features[i].get('gpx_extensions') === 'object' && fileData.text.indexOf('<trkseg>') > -1 && fileData.text.indexOf('</trkseg>') > fileData.text.indexOf('<trkseg>')) {
                //gpx track
                //for now we keep track as track
                //in the future we could add an extra dropdown where the user can choose 'export as gpx route' or 'export as gpx track'
                //and then we need to add an if/else inside this if
                /*
                if(user wants to keep track) {
                */

                //at this moment we don't need to check if there is already a name/desc in the tag, because during import name/desc are removed (possibly first copied to metadata, see importGpxFile)
                fileData.text = fileData.text.substring(0, fileData.text.indexOf('<trk>') + 5) + nameAndDescription + fileData.text.substring(fileData.text.indexOf('<trk>') + 5);

                const gpx_extensions: {} = features[i].get('gpx_extensions');
                let trkseg_extension = '';

                for (let key in gpx_extensions) {
                  switch (key) {
                    case 'trkseg_fixedpoint_indexes': {
                      //second parameter false
                      trkseg_extension += this._convertFixedPointIndexesToGpxExtensionTag(gpx_extensions[key], false);
                      break;
                    }
                    //add other extensions if necessary
                  }
                }

                fileData.text = fileData.text.substring(0, fileData.text.indexOf('</trkseg>')) + (trkseg_extension.length > 0 ? '<extensions>' + trkseg_extension + '</extensions>' : '') + fileData.text.substring(fileData.text.indexOf('</trkseg>'));

                /*
                } else { 
                  //user wants a route
                 //converts track to route
                 //don't forget to add nameAndDescription, at this moment we don't need to check if there is already a name/desc in the tag, because during import name/desc are removed (possibly first copied to metadata, see importGpxFile)
                }
               */
              }
            }
          }

          if (this.data.metadata) {
            //name and description are already looked up above
            let metadataText = nameAndDescription;
            metadataText += this.data.metadata && typeof this.data.metadata.time === 'number' && !isNaN(new Date(this.data.metadata.time).getTime()) ? '<time>' + new Date(this.data.metadata.time).toISOString() + '</time>' : '';
            if (metadataText.length > 0) {
              fileData.text = fileData.text.substring(0, fileData.text.indexOf('>') + 1) + '<metadata>' + metadataText + '</metadata>' + fileData.text.substring(fileData.text.indexOf('>') + 1);
            }
          }

          //wpt desc tags cannot contain html tags, so replacing < and > by html entities
          let updateWpts = '';
          while (fileData.text.indexOf("<wpt>") > -1 && fileData.text.indexOf("</wpt>") > fileData.text.indexOf("<wpt>")) {
            updateWpts = fileData.text.substring(0, fileData.text.indexOf("<wpt>"));
            let wptData = fileData.text.substring(fileData.text.indexOf("<wpt>"), fileData.text.indexOf("</wpt>") + 6);
            fileData.text = fileData.text.substring(fileData.text.indexOf("</wpt>") + 6);

            if (wptData.indexOf("<desc>") > -1 && wptData.indexOf("</desc>") > wptData.indexOf("<desc>")) {
              wptData = wptData.substring(0, wptData.indexOf("<desc>") + 6) + wptData.substring(wptData.indexOf("<desc>") + 6, wptData.indexOf("</desc>")).replaceAll('<', '&lt;').replaceAll('>', '&gt;') + wptData.substring(wptData.indexOf("</desc>"));
            }

            updateWpts += wptData;
          }
          fileData.text = updateWpts + fileData.text;

          //finally pretty print
          //some users like to edit their gpx manually
          fileData.text = this._xmlPrettyPrint(fileData.text);
        }
        fileData.contentType = 'application/gpx+xml;charset=utf-8';
        break;
      }
      case FileDownloaderSupportedFileTypes.KML: {
        const writer = new OlFormats.KML();
        const kmlOutput = writer.writeFeaturesNode(features, writerOptions);
        //output from writeFeatures creates an error in google earth
        //fileData.text = writer.writeFeatures(features, writerOptions);
        fileData.text = this._xmlToString(kmlOutput);

        if (typeof fileData.text === 'string' && fileData.text.indexOf('>') > -1) {
          //finally pretty print
          //some users like to edit their kml manually
          fileData.text = this._xmlPrettyPrint(fileData.text);
        }

        fileData.contentType = 'application/vnd.google-earth.kml+xml;charset=utf-8';
        break;
      }
      case FileDownloaderSupportedFileTypes.WKT: {
        const writer = new OlFormats.WKT();
        fileData.text = writer.writeFeatures(features, writerOptions);
        fileData.contentType = 'text/plain;charset=utf-8';
        break;
      }
      default: {
        console.log(`ERROR: download of file type "${this._fileDownloadForm.value.filetype}" is not yet implemented`);
      }
    }

    return fileData;
  }

  private _convertFixedPointIndexesToGpxExtensionTag(fixedPointIndexes: number[], isRouteExtension: boolean): string {
    let extensionTag = '';
    if (Array.isArray(fixedPointIndexes) && fixedPointIndexes.length > 0) {
      extensionTag += isRouteExtension ? '<tmv:rte_fixedpoint_indexes>' : '<tmv:trkseg_fixedpoint_indexes>';
      fixedPointIndexes.forEach((rte_fixedpoint_index: number) => {
        extensionTag += '<tmv:num>' + rte_fixedpoint_index + '</tmv:num>';
      });
      extensionTag += isRouteExtension ? '</tmv:rte_fixedpoint_indexes>' : '</tmv:trkseg_fixedpoint_indexes>';
    }
    if (extensionTag.length > 0) {
      extensionTag = isRouteExtension ? '<tmv:rte_extension>' + extensionTag + '</tmv:rte_extension>' : '<tmv:trkseg_extension>' + extensionTag + '</tmv:trkseg_extension>';
    }
    return extensionTag;
  }

  /**
   * 
   * @param xmlData normally of type Node
   * @returns 
   */
  private _xmlToString(xmlData: any): string {
    var xmlString = '';
    if (window['ActiveXObject']) {
      //IE
      xmlString = xmlData.xml;
    } else {
      xmlString = (new XMLSerializer()).serializeToString(xmlData);
    }
    return xmlString;
  }

  /**
  * 
  * @param xml xml string
  * @param indentChars default is '\t' , you could replace it with e.g. space(s) ' '
  * @param newLineChars default is '\r\n' , you could replace it with e.g. '\n' or empty styring for no new line
  * @returns 
  */
  private _xmlPrettyPrint(xml: String, indentChars: String = '\t', newLineChars: String = '\r\n') {
    let formatted = '', indent = '';
    const nodes = xml.slice(1, -1).split(/>\s*</);
    if (nodes[0][0] == '?') formatted += '<' + nodes.shift() + '>' + newLineChars;
    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i];
      if (node[0] == '/') indent = indent.slice(indentChars.length); // decrease indent
      formatted += indent + '<' + node + '>' + newLineChars;
      if (node[0] != '/' && node[node.length - 1] != '/' && node.indexOf('</') == -1) indent += indentChars; // increase indent
    }
    return formatted;
  }

  private _isValidFeatureForExport(feature: Feature, vectorLayerId: String) {
    let isValidFeatureForExport = true;

    switch (this._fileDownloadForm.value.filetype) {
      case FileDownloaderSupportedFileTypes.GPX: {
        if (!/^(linestring|multilinestring|point|multipoint)$/i.test(feature.getGeometry().getType())) {
          //gpx files don't support polygons, circles
          isValidFeatureForExport = false
        }
        break;
      }
    }

    if (isValidFeatureForExport) {
      //tt specific points are not added
      switch (vectorLayerId) {
        case 'traceDrawLayerPoint':
        case 'traceDrawLayerLineString': {
          isValidFeatureForExport = !feature.get('fileDownloadIgnore');
          break;
        }
        //other known tt layers?
      }
    }

    return isValidFeatureForExport
  }

  private _removeKnownTTPropertiesFromFeature(feature: Feature, vectorLayerId: String, fileDownloaderSupportedFileType: FileDownloaderSupportedFileTypes) {
    //tt specific properties are not added
    switch (vectorLayerId) {
      case 'traceDrawLayerPoint': {
        if (typeof feature.get('wayPoint_ref') === 'number' || typeof feature.get('wayPoint_ref') === 'string') {
          feature.unset('wayPoint_ref');
        }
        break;
      }
      case 'traceDrawLayerLineString': {
        if (typeof feature.get('route_ref') === 'number' || typeof feature.get('route_ref') === 'string' ) {
          feature.unset('route_ref');
        }
        break;
      }
      //other known tt layers?
    }

    if (feature.get('gpx_extensions') && FileDownloaderSupportedFileTypes.GPX !== fileDownloaderSupportedFileType) {
      feature.unset('gpx_extensions');
    }
  }

}
